import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Users, Calendar, DollarSign, TrendingUp, 
  AlertCircle, CheckCircle, Clock, Search, RefreshCw,
  BarChart3, Activity, Globe, Shield, Settings, Key, Mail, Lock, Eye, EyeOff, Save,
  Download, X, FileSpreadsheet, AlertTriangle, CreditCard, Phone, Target, XCircle, Trash2,
  Edit, Check
} from 'lucide-react';
import MarketingPanel from './MarketingPanel';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Excel Export Utility
const exportToExcel = (data, filename) => {
  // Convert data to CSV format
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(h => {
        const val = row[h];
        // Escape commas and quotes
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val ?? '';
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  toast.success(`Exported ${data.length} records`);
};

// Stats Detail Modal Component
const StatsDetailModal = ({ isOpen, onClose, title, data, columns, onExport }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={onExport}
              className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        <div className="overflow-auto max-h-[60vh] p-4">
          {data && data.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  {columns.map((col, i) => (
                    <th key={i} className="text-left py-2 px-3 text-slate-400 font-medium text-sm">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    {columns.map((col, j) => (
                      <td key={j} className="py-2 px-3 text-slate-300 text-sm">
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-slate-400 text-center py-8">No data available</p>
          )}
        </div>
        
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-sm text-slate-400">{data?.length || 0} records</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircle },
    trial: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: Clock },
    suspended: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: AlertCircle },
    pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: Clock },
    cancelled: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200', icon: AlertCircle }
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </Badge>
  );
};

// Revenue Detail Modal Component
const RevenueDetailModal = ({ isOpen, onClose, token }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchRevenueDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/tenant/stats/revenue-details?from_date=${fromDate}T00:00:00Z&to_date=${toDate}T23:59:59Z`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRevenueData(data);
      }
    } catch (error) {
      console.error('Failed to fetch revenue details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRevenueDetails();
    }
  }, [isOpen, fromDate, toDate]);

  const handleExport = () => {
    if (!revenueData?.payments?.length) {
      toast.error('No data to export');
      return;
    }
    exportToExcel(revenueData.payments.map(p => ({
      Date: p.date,
      Company: p.company_name,
      Plan: p.plan,
      Billing_Cycle: p.billing_cycle,
      Gross_Amount: p.gross_amount,
      Net_Amount: p.net_amount,
      Order_ID: p.order_id
    })), 'revenue_report');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h3 className="text-xl font-bold text-white">Revenue Details</h3>
            <p className="text-sm text-slate-400">
              {revenueData?.environment === 'live' ? 'LIVE' : 'TEST'} Environment
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleExport}
              className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Date Filters */}
        <div className="p-4 border-b border-slate-700 flex flex-wrap items-center gap-4 bg-slate-800/50">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-400">From:</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-400">To:</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <Button 
            size="sm" 
            onClick={fetchRevenueDetails}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Apply
          </Button>
        </div>

        {/* Summary Cards */}
        {revenueData && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-slate-700">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <p className="text-xs text-emerald-400">Gross Revenue</p>
              <p className="text-xl font-bold text-emerald-300">{formatCurrency(revenueData.total_gross)}</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-xs text-red-400">Razorpay Fee ({revenueData.razorpay_fee_percent}%)</p>
              <p className="text-xl font-bold text-red-300">-{formatCurrency(revenueData.total_fee)}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-400">Net Revenue</p>
              <p className="text-xl font-bold text-blue-300">{formatCurrency(revenueData.total_net)}</p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <p className="text-xs text-purple-400">Total Payments</p>
              <p className="text-xl font-bold text-purple-300">{revenueData.total_payments}</p>
            </div>
          </div>
        )}

        {/* Payments Table */}
        <div className="overflow-auto max-h-[45vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
            </div>
          ) : revenueData?.payments?.length ? (
            <table className="w-full">
              <thead className="bg-slate-700/50 sticky top-0">
                <tr>
                  <th className="py-3 px-4 text-left text-slate-300 text-sm font-medium">Date</th>
                  <th className="py-3 px-4 text-left text-slate-300 text-sm font-medium">Company</th>
                  <th className="py-3 px-4 text-left text-slate-300 text-sm font-medium">Plan</th>
                  <th className="py-3 px-4 text-left text-slate-300 text-sm font-medium">Cycle</th>
                  <th className="py-3 px-4 text-right text-slate-300 text-sm font-medium">Gross</th>
                  <th className="py-3 px-4 text-right text-slate-300 text-sm font-medium">Net</th>
                  <th className="py-3 px-4 text-left text-slate-300 text-sm font-medium">Order ID</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.payments.map((payment, i) => (
                  <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-3 px-4 text-slate-300 text-sm">{payment.date}</td>
                    <td className="py-3 px-4 text-white text-sm font-medium">{payment.company_name}</td>
                    <td className="py-3 px-4">
                      <Badge className="bg-teal-500/20 text-teal-300 capitalize">
                        {payment.plan}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-sm capitalize">{payment.billing_cycle}</td>
                    <td className="py-3 px-4 text-emerald-400 text-sm text-right font-medium">
                      {formatCurrency(payment.gross_amount)}
                    </td>
                    <td className="py-3 px-4 text-blue-400 text-sm text-right font-medium">
                      {formatCurrency(payment.net_amount)}
                    </td>
                    <td className="py-3 px-4 text-slate-500 text-xs font-mono">{payment.order_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-slate-400 text-center py-8">No payments found in selected date range</p>
          )}
        </div>

        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-sm text-slate-400">
            Showing {revenueData?.payments?.length || 0} payments from {fromDate} to {toDate}
          </p>
        </div>
      </div>
    </div>
  );
};

// Settings Panel Component
const SettingsPanel = ({ user, token }) => {
  const [showRazorpayKey, setShowRazorpayKey] = useState(false);
  const [showRazorpaySecret, setShowRazorpaySecret] = useState(false);
  const [showRazorpayTestKey, setShowRazorpayTestKey] = useState(false);
  const [showRazorpayTestSecret, setShowRazorpayTestSecret] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [settings, setSettings] = useState({
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_test_key_id: '',
    razorpay_test_key_secret: '',
    resend_api_key: '',
    email_from: 'noreply@wellnesttechnologies.com',
    trial_days: 7,
    reminder_days_before: 7,
    platform_mode: 'test'  // test or live
  });
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password-direct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new
        })
      });
      if (res.ok) {
        toast.success('Password changed successfully! Confirmation email sent.');
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Payment Gateway Settings */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-emerald-400" />
            Payment Gateway (Razorpay)
          </CardTitle>
          <CardDescription className="text-slate-400">
            Configure Razorpay API keys and platform mode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Platform Mode Toggle */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <label className="text-sm font-medium text-white mb-3 block">Platform Mode</label>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${
                settings.platform_mode === 'test' 
                  ? 'border-yellow-500 bg-yellow-500/10' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}>
                <input 
                  type="radio" 
                  name="platform_mode" 
                  value="test"
                  checked={settings.platform_mode === 'test'}
                  onChange={(e) => setSettings(prev => ({ ...prev, platform_mode: e.target.value }))}
                  className="hidden"
                />
                <AlertTriangle className={`w-4 h-4 ${settings.platform_mode === 'test' ? 'text-yellow-400' : 'text-slate-500'}`} />
                <span className={settings.platform_mode === 'test' ? 'text-yellow-400 font-medium' : 'text-slate-400'}>Test Mode</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${
                settings.platform_mode === 'live' 
                  ? 'border-emerald-500 bg-emerald-500/10' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}>
                <input 
                  type="radio" 
                  name="platform_mode" 
                  value="live"
                  checked={settings.platform_mode === 'live'}
                  onChange={(e) => setSettings(prev => ({ ...prev, platform_mode: e.target.value }))}
                  className="hidden"
                />
                <CheckCircle className={`w-4 h-4 ${settings.platform_mode === 'live' ? 'text-emerald-400' : 'text-slate-500'}`} />
                <span className={settings.platform_mode === 'live' ? 'text-emerald-400 font-medium' : 'text-slate-400'}>Live Mode</span>
              </label>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {settings.platform_mode === 'test' 
                ? '⚠️ Test Mode: No subscriptions, trials, or payments will be processed. All checkout attempts will fail.'
                : '✓ Live Mode: Subscriptions, free trials, and payments are active.'}
            </p>
          </div>
          
          {/* Live Keys Section */}
          <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
            <h4 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Live Keys (Real Payments)
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Razorpay Live Key ID</label>
                <div className="relative">
                  <Input
                    type={showRazorpayKey ? "text" : "password"}
                    value={settings.razorpay_key_id}
                    onChange={(e) => setSettings(prev => ({ ...prev, razorpay_key_id: e.target.value }))}
                    placeholder="rzp_live_xxxxxxxxxxxx"
                    className="bg-slate-900 border-slate-600 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRazorpayKey(!showRazorpayKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showRazorpayKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Razorpay Live Secret</label>
                <div className="relative">
                  <Input
                    type={showRazorpaySecret ? "text" : "password"}
                    value={settings.razorpay_key_secret}
                    onChange={(e) => setSettings(prev => ({ ...prev, razorpay_key_secret: e.target.value }))}
                    placeholder="xxxxxxxxxxxxxxxxxxxx"
                    className="bg-slate-900 border-slate-600 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRazorpaySecret(!showRazorpaySecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showRazorpaySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Test Keys Section */}
          <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Test Keys (No Real Charges)
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Razorpay Test Key ID</label>
                <div className="relative">
                  <Input
                    type={showRazorpayTestKey ? "text" : "password"}
                    value={settings.razorpay_test_key_id}
                    onChange={(e) => setSettings(prev => ({ ...prev, razorpay_test_key_id: e.target.value }))}
                    placeholder="rzp_test_xxxxxxxxxxxx"
                    className="bg-slate-900 border-slate-600 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRazorpayTestKey(!showRazorpayTestKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showRazorpayTestKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Razorpay Test Secret</label>
                <div className="relative">
                  <Input
                    type={showRazorpayTestSecret ? "text" : "password"}
                    value={settings.razorpay_test_key_secret}
                    onChange={(e) => setSettings(prev => ({ ...prev, razorpay_test_key_secret: e.target.value }))}
                    placeholder="xxxxxxxxxxxxxxxxxxxx"
                    className="bg-slate-900 border-slate-600 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRazorpayTestSecret(!showRazorpayTestSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showRazorpayTestSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-amber-400/70">
                Test Cards: <span className="font-mono">4111 1111 1111 1111</span> (success), <span className="font-mono">4000 0000 0000 0002</span> (failure)
              </p>
            </div>
          </div>
          
          <div className="pt-2">
            <p className="text-sm text-slate-400 p-3 bg-slate-700/50 rounded-lg">
              {settings.platform_mode === 'test' 
                ? "🧪 Test Mode Active: Using test keys. No real payments will be processed." 
                : "🟢 Live Mode Active: Using live keys. Real payments will be processed."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" />
            Email Configuration (Resend)
          </CardTitle>
          <CardDescription className="text-slate-400">
            Configure email notifications using Resend
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Resend API Key</label>
            <div className="relative">
              <Input
                type={showResendKey ? "text" : "password"}
                value={settings.resend_api_key}
                onChange={(e) => setSettings(prev => ({ ...prev, resend_api_key: e.target.value }))}
                placeholder="re_xxxxxxxxxxxx"
                className="bg-slate-900 border-slate-600 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowResendKey(!showResendKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Get your key from resend.com/api-keys</p>
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">From Email Address</label>
            <Input
              type="email"
              value={settings.email_from}
              onChange={(e) => setSettings(prev => ({ ...prev, email_from: e.target.value }))}
              placeholder="noreply@wellnesttechnologies.com"
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Free Trial Duration (days)</label>
            <Input
              type="number"
              value={settings.trial_days}
              onChange={(e) => setSettings(prev => ({ ...prev, trial_days: parseInt(e.target.value) }))}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Reminder Days Before Expiry</label>
            <Input
              type="number"
              value={settings.reminder_days_before}
              onChange={(e) => setSettings(prev => ({ ...prev, reminder_days_before: parseInt(e.target.value) }))}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div className="pt-2">
            <Badge className={settings.resend_api_key ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}>
              {settings.resend_api_key ? "Email Enabled" : "Email Disabled (Key Not Set)"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-400" />
            Change Password
          </CardTitle>
          <CardDescription className="text-slate-400">
            Update your admin password
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Current Password</label>
            <Input
              type="password"
              value={passwords.current}
              onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">New Password</label>
            <Input
              type="password"
              value={passwords.new}
              onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Confirm New Password</label>
            <Input
              type="password"
              value={passwords.confirm}
              onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwords.current || !passwords.new}
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>

      {/* Save All Settings Button */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Save className="w-5 h-5 text-emerald-400" />
            Save Configuration
          </CardTitle>
          <CardDescription className="text-slate-400">
            Save all platform settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {savingSettings ? 'Saving...' : 'Save All Settings'}
          </Button>
          <p className="text-xs text-slate-500 mt-3 text-center">
            Settings are encrypted and stored securely
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Payments Panel Component
const PaymentsPanel = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('logs');

  useEffect(() => {
    fetchPaymentData();
  }, [statusFilter]);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [logsRes, statsRes, subsRes] = await Promise.all([
        fetch(`${API_URL}/api/tenant/payments/logs${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tenant/payments/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tenant/subscriptions/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (logsRes.ok) {
        const data = await logsRes.json();
        setPayments(data.payments || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setPaymentStats(data);
      }
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-500/20 text-emerald-400',
      created: 'bg-yellow-500/20 text-yellow-400',
      failed: 'bg-red-500/20 text-red-400',
      expired: 'bg-slate-500/20 text-slate-400',
      active: 'bg-emerald-500/20 text-emerald-400',
      trial: 'bg-blue-500/20 text-blue-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-500/20 text-slate-400'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Payment Stats */}
      {paymentStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <CheckCircle className="w-6 h-6 mx-auto text-emerald-400 mb-2" />
                <p className="text-2xl font-bold text-white">{paymentStats.total_success}</p>
                <p className="text-xs text-slate-400">Successful</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <AlertCircle className="w-6 h-6 mx-auto text-red-400 mb-2" />
                <p className="text-2xl font-bold text-white">{paymentStats.total_failed}</p>
                <p className="text-xs text-slate-400">Failed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <Clock className="w-6 h-6 mx-auto text-yellow-400 mb-2" />
                <p className="text-2xl font-bold text-white">{paymentStats.total_pending}</p>
                <p className="text-xs text-slate-400">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <DollarSign className="w-6 h-6 mx-auto text-teal-400 mb-2" />
                <p className="text-2xl font-bold text-white">₹{paymentStats.gross_revenue?.toLocaleString() || paymentStats.month_revenue?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400">Gross Revenue</p>
                {paymentStats.razorpay_fee > 0 && (
                  <div className="mt-1 text-xs">
                    <span className="text-red-400">-₹{paymentStats.razorpay_fee?.toLocaleString()}</span>
                    <span className="text-slate-500"> ({paymentStats.razorpay_fee_percent || 2}% fee)</span>
                  </div>
                )}
                {paymentStats.net_revenue > 0 && (
                  <p className="text-xs text-emerald-400 mt-1">Net: ₹{paymentStats.net_revenue?.toLocaleString()}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <Users className="w-6 h-6 mx-auto text-blue-400 mb-2" />
                <p className="text-2xl font-bold text-white">{paymentStats.active_subscriptions}</p>
                <p className="text-xs text-slate-400">Active Subs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-center">
                <AlertCircle className="w-6 h-6 mx-auto text-orange-400 mb-2" />
                <p className="text-2xl font-bold text-white">{paymentStats.expiring_soon}</p>
                <p className="text-xs text-slate-400">Expiring Soon</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Logs and Subscriptions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Payment Management</CardTitle>
              <CardDescription className="text-slate-400">
                View all payment transactions and subscriptions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant={activeTab === 'logs' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('logs')}
                className={activeTab === 'logs' ? 'bg-teal-600' : 'border-slate-600 text-slate-300'}
              >
                Payment Logs
              </Button>
              <Button 
                variant={activeTab === 'subscriptions' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('subscriptions')}
                className={activeTab === 'subscriptions' ? 'bg-teal-600' : 'border-slate-600 text-slate-300'}
              >
                Subscriptions
              </Button>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-700/50 border border-slate-600 rounded-md px-3 py-1.5 text-sm text-white"
              >
                <option value="">All Status</option>
                <option value="paid">Paid</option>
                <option value="created">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <Button variant="outline" size="icon" onClick={fetchPaymentData}
                className="border-slate-600 hover:bg-slate-700">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
              <p className="text-slate-400 mt-4">Loading...</p>
            </div>
          ) : activeTab === 'logs' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Order ID</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Company</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Amount</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-slate-400">
                        No payment records found
                      </td>
                    </tr>
                  ) : payments.map((payment, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4">
                        <code className="text-xs text-slate-300 bg-slate-700 px-2 py-1 rounded">
                          {payment.order_id?.slice(-12)}
                        </code>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{payment.company_name || '-'}</p>
                        <p className="text-xs text-slate-400">{payment.admin_email}</p>
                      </td>
                      <td className="py-3 px-4 text-slate-300">{payment.plan?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-right text-white font-semibold">₹{payment.amount}</td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(payment.status)}</td>
                      <td className="py-3 px-4 text-slate-400 text-sm">{formatDate(payment.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Company</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Amount</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Period End</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-slate-400">
                        No active subscriptions found
                      </td>
                    </tr>
                  ) : subscriptions.map((sub, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{sub.company_name || sub.company_id}</p>
                      </td>
                      <td className="py-3 px-4 text-slate-300">{sub.plan?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-right text-white font-semibold">₹{sub.amount}/mo</td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(sub.status)}</td>
                      <td className="py-3 px-4 text-slate-400 text-sm">{formatDate(sub.current_period_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Pricing Panel Component - Uses plan_pricing collection
// Super Admin edits MONTHLY prices, YEARLY auto-calculated from discount %
const PricingPanel = ({ token }) => {
  const [pricing, setPricing] = useState([]);
  const [settings, setSettings] = useState({ trial_days: 7, annual_discount_percent: 17 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPrices, setEditPrices] = useState({
    starter: { price: 0, locations_limit: 3, therapists_limit: 10 },
    professional: { price: 0, locations_limit: 10, therapists_limit: 50 },
    enterprise: { price: 0, locations_limit: -1, therapists_limit: -1 }
  });

  const PLAN_TIERS = ['starter', 'professional', 'enterprise'];

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPricing(data.pricing || []);
        setSettings(data.settings || { trial_days: 7, annual_discount_percent: 17 });
        
        // Extract monthly prices for editing
        const monthlyPrices = {};
        (data.pricing || []).forEach(p => {
          if (p.billing_cycle === 'monthly') {
            monthlyPrices[p.plan_tier] = {
              price: p.price,
              locations_limit: p.locations_limit,
              therapists_limit: p.therapists_limit
            };
          }
        });
        setEditPrices(prev => ({ ...prev, ...monthlyPrices }));
      } else {
        await fetch(`${API_URL}/api/admin/pricing/seed-defaults`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        await fetchPricing();
      }
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const calculateYearlyPrice = (monthlyPrice) => {
    const discount = settings.annual_discount_percent || 17;
    return Math.round(monthlyPrice * 12 * (100 - discount) / 100);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Save each plan's monthly price and limits
      for (const tier of PLAN_TIERS) {
        const tierData = editPrices[tier];
        
        // Update monthly plan
        await fetch(`${API_URL}/api/admin/pricing/update-tier`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            plan_tier: tier,
            monthly_price: tierData.price,
            locations_limit: tierData.locations_limit,
            therapists_limit: tierData.therapists_limit
          })
        });
      }
      
      // Also update settings
      await fetch(`${API_URL}/api/admin/pricing-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      toast.success('All pricing updated successfully!');
      setEditMode(false);
      await fetchPricing();
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Card - Annual Discount */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-teal-400" />
            Platform Pricing Settings
          </CardTitle>
          <CardDescription className="text-slate-400">
            Set monthly prices below. Annual prices are auto-calculated using the discount %.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Trial Days</Label>
              <Input
                type="number"
                value={settings.trial_days}
                onChange={(e) => setSettings({ ...settings, trial_days: parseInt(e.target.value) || 7 })}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                disabled={!editMode}
              />
            </div>
            <div>
              <Label className="text-slate-300">Annual Discount %</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={settings.annual_discount_percent}
                  onChange={(e) => setSettings({ ...settings, annual_discount_percent: parseInt(e.target.value) || 17 })}
                  className="bg-slate-900 border-slate-700 text-white"
                  disabled={!editMode}
                  min={0}
                  max={50}
                />
                <Badge className="bg-green-500/20 text-green-400 whitespace-nowrap">
                  Save {settings.annual_discount_percent}%
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Annual = Monthly × 12 × {100 - (settings.annual_discount_percent || 17)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Mode Toggle */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Plan Pricing</h3>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)} className="bg-slate-700 hover:bg-slate-600">
            <Edit className="w-4 h-4 mr-2" /> Edit Prices
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => setEditMode(false)} variant="ghost" className="text-slate-400">
              Cancel
            </Button>
            <Button onClick={handleSaveAll} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          </div>
        )}
      </div>

      {/* Pricing Grid - Three Column Layout */}
      <div className="grid md:grid-cols-3 gap-4">
        {PLAN_TIERS.map((tier, index) => {
          const tierData = editPrices[tier] || { price: 0, locations_limit: 3, therapists_limit: 10 };
          const monthlyPrice = tierData.price;
          const yearlyPrice = calculateYearlyPrice(monthlyPrice);
          
          return (
            <Card 
              key={tier} 
              className={`bg-slate-800/50 border-slate-700 relative ${
                index === 1 ? 'ring-2 ring-teal-500' : ''
              }`}
            >
              {index === 1 && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-white capitalize flex items-center gap-2">
                  {tier === 'enterprise' && <Badge className="bg-purple-500">Premium</Badge>}
                  {tier}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Monthly Price */}
                <div className={`p-3 rounded-lg ${editMode ? 'bg-teal-900/20 border border-teal-500/30' : 'bg-slate-900/50'}`}>
                  <Label className="text-slate-400 text-xs flex items-center gap-1">
                    Monthly Price {editMode && <span className="text-teal-400">(Editable)</span>}
                  </Label>
                  {editMode ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-400">₹</span>
                      <Input
                        type="number"
                        value={tierData.price}
                        onChange={(e) => setEditPrices({
                          ...editPrices,
                          [tier]: { ...tierData, price: parseInt(e.target.value) || 0 }
                        })}
                        className="bg-slate-800 border-slate-600 text-white h-10 text-lg font-bold"
                      />
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-teal-400 mt-1">
                      {formatCurrency(monthlyPrice)}<span className="text-sm text-slate-400">/mo</span>
                    </p>
                  )}
                </div>

                {/* Yearly Price - Auto Calculated */}
                <div className="p-3 rounded-lg bg-slate-900/50">
                  <Label className="text-slate-400 text-xs flex items-center gap-1">
                    Annual Price <Badge className="bg-green-500/20 text-green-400 text-xs ml-1">Auto</Badge>
                  </Label>
                  <p className="text-xl font-bold text-white mt-1">
                    {formatCurrency(yearlyPrice)}<span className="text-sm text-slate-400">/yr</span>
                  </p>
                  <p className="text-xs text-green-400 mt-1">
                    Save {formatCurrency(monthlyPrice * 12 - yearlyPrice)} ({settings.annual_discount_percent}% off)
                  </p>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-400 text-xs flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Locations
                    </Label>
                    {editMode ? (
                      <Input
                        type="number"
                        value={tierData.locations_limit}
                        onChange={(e) => setEditPrices({
                          ...editPrices,
                          [tier]: { ...tierData, locations_limit: parseInt(e.target.value) }
                        })}
                        className="bg-slate-800 border-slate-600 text-white h-8 mt-1"
                        placeholder="-1 = ∞"
                      />
                    ) : (
                      <p className="text-white font-semibold mt-1">
                        {tierData.locations_limit === -1 ? '∞ Unlimited' : tierData.locations_limit}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs flex items-center gap-1">
                      <Users className="w-3 h-3" /> Staff
                    </Label>
                    {editMode ? (
                      <Input
                        type="number"
                        value={tierData.therapists_limit}
                        onChange={(e) => setEditPrices({
                          ...editPrices,
                          [tier]: { ...tierData, therapists_limit: parseInt(e.target.value) }
                        })}
                        className="bg-slate-800 border-slate-600 text-white h-8 mt-1"
                        placeholder="-1 = ∞"
                      />
                    ) : (
                      <p className="text-white font-semibold mt-1">
                        {tierData.therapists_limit === -1 ? '∞ Unlimited' : tierData.therapists_limit}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-blue-400 text-sm">
          <strong>How it works:</strong> Edit the monthly price for each plan. The annual price is automatically calculated as: 
          Monthly × 12 × (100% - {settings.annual_discount_percent}% discount) = Annual Price
        </p>
      </div>
    </div>
  );
};

// Reports Panel Component with Date Filters and Export
const ReportsPanel = ({ token }) => {
  const [reportType, setReportType] = useState('revenue');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tenant/reports/${reportType}?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [reportType, dateRange]);

  const exportReport = () => {
    if (!reportData?.items) return;
    const filename = `${reportType}_report_${dateRange.start}_to_${dateRange.end}`;
    exportToExcel(reportData.items, filename);
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  const setMonthRange = (monthsAgo) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - monthsAgo + 1, 0);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  return (
    <div className="space-y-6">
      {/* Report Type & Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            Platform Reports
          </CardTitle>
          <CardDescription className="text-slate-400">
            Generate and export historical reports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Report Type Selection */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={reportType === 'revenue' ? 'default' : 'outline'}
              onClick={() => setReportType('revenue')}
              className={reportType === 'revenue' ? 'bg-emerald-600' : 'border-slate-600'}
            >
              <DollarSign className="w-4 h-4 mr-1" /> Revenue Report
            </Button>
            <Button
              size="sm"
              variant={reportType === 'subscriptions' ? 'default' : 'outline'}
              onClick={() => setReportType('subscriptions')}
              className={reportType === 'subscriptions' ? 'bg-emerald-600' : 'border-slate-600'}
            >
              <CreditCard className="w-4 h-4 mr-1" /> Subscriptions
            </Button>
            <Button
              size="sm"
              variant={reportType === 'bookings' ? 'default' : 'outline'}
              onClick={() => setReportType('bookings')}
              className={reportType === 'bookings' ? 'bg-emerald-600' : 'border-slate-600'}
            >
              <Calendar className="w-4 h-4 mr-1" /> Bookings
            </Button>
            <Button
              size="sm"
              variant={reportType === 'tenants' ? 'default' : 'outline'}
              onClick={() => setReportType('tenants')}
              className={reportType === 'tenants' ? 'bg-emerald-600' : 'border-slate-600'}
            >
              <Building2 className="w-4 h-4 mr-1" /> Tenants
            </Button>
          </div>

          {/* Date Range */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">From:</span>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">To:</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
              />
            </div>
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => setQuickRange(7)} className="text-xs">
              Last 7 Days
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setQuickRange(30)} className="text-xs">
              Last 30 Days
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setQuickRange(90)} className="text-xs">
              Last 90 Days
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMonthRange(0)} className="text-xs">
              This Month
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMonthRange(1)} className="text-xs">
              Last Month
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMonthRange(2)} className="text-xs">
              2 Months Ago
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Summary */}
      {reportData && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg">
                {reportType === 'revenue' && 'Revenue Summary'}
                {reportType === 'subscriptions' && 'Subscription Summary'}
                {reportType === 'bookings' && 'Bookings Summary'}
                {reportType === 'tenants' && 'Tenant Summary'}
              </CardTitle>
              <Button
                size="sm"
                onClick={exportReport}
                disabled={!reportData?.items?.length}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" /> Export Excel
              </Button>
            </div>
            <p className="text-xs text-slate-400">
              {dateRange.start} to {dateRange.end}
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {reportType === 'revenue' && (
                    <>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">MRR (Platform Revenue)</p>
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(reportData.summary?.mrr || 0)}</p>
                        <p className="text-xs text-blue-400 mt-1">Your subscription income</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">ARR (Annual)</p>
                        <p className="text-2xl font-bold text-blue-400">{formatCurrency(reportData.summary?.arr || 0)}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Revenue Processed (GMV)</p>
                        <p className="text-2xl font-bold text-purple-400">{formatCurrency(reportData.summary?.gmv || 0)}</p>
                        <p className="text-xs text-slate-500 mt-1">Tenants' total business</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Active Payers</p>
                        <p className="text-2xl font-bold text-white">{reportData.summary?.active_payers || 0}</p>
                      </div>
                    </>
                  )}
                  {reportType === 'subscriptions' && (
                    <>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">New Subscriptions</p>
                        <p className="text-2xl font-bold text-emerald-400">{reportData.summary?.new_subscriptions || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Renewals</p>
                        <p className="text-2xl font-bold text-blue-400">{reportData.summary?.renewals || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Churned</p>
                        <p className="text-2xl font-bold text-red-400">{reportData.summary?.churned || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Revenue Collected</p>
                        <p className="text-2xl font-bold text-white">{formatCurrency(reportData.summary?.revenue || 0)}</p>
                      </div>
                    </>
                  )}
                  {reportType === 'bookings' && (
                    <>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Total Bookings</p>
                        <p className="text-2xl font-bold text-emerald-400">{reportData.summary?.total_bookings || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Completed</p>
                        <p className="text-2xl font-bold text-green-400">{reportData.summary?.completed || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Cancelled</p>
                        <p className="text-2xl font-bold text-red-400">{reportData.summary?.cancelled || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">GMV Processed</p>
                        <p className="text-2xl font-bold text-purple-400">{formatCurrency(reportData.summary?.gmv || 0)}</p>
                      </div>
                    </>
                  )}
                  {reportType === 'tenants' && (
                    <>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">New Tenants</p>
                        <p className="text-2xl font-bold text-emerald-400">{reportData.summary?.new_tenants || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Active</p>
                        <p className="text-2xl font-bold text-green-400">{reportData.summary?.active || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Trial</p>
                        <p className="text-2xl font-bold text-blue-400">{reportData.summary?.trial || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400">Expired</p>
                        <p className="text-2xl font-bold text-red-400">{reportData.summary?.expired || 0}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Data Table */}
                {reportData.items?.length > 0 && (
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-800">
                        <tr className="border-b border-slate-700">
                          {Object.keys(reportData.items[0]).map(key => (
                            <th key={key} className="text-left py-2 px-3 text-slate-400 font-medium capitalize">
                              {key.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.items.slice(0, 50).map((item, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            {Object.values(item).map((val, i) => (
                              <td key={i} className="py-2 px-3 text-white">
                                {typeof val === 'number' && val > 100 ? formatCurrency(val) : String(val ?? '-')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reportData.items.length > 50 && (
                      <p className="text-center text-xs text-slate-400 py-2">
                        Showing 50 of {reportData.items.length} records. Export for full data.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function SuperAdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  
  // Tenant sub-tabs state
  const [tenantSubTab, setTenantSubTab] = useState('registered');
  const [abandonedLeads, setAbandonedLeads] = useState([]);
  const [abandonedSearch, setAbandonedSearch] = useState('');
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistSearch, setBlacklistSearch] = useState('');
  const [cancelledTenants, setCancelledTenants] = useState([]);
  const [cancelledSearch, setCancelledSearch] = useState('');
  
  // Revenue modal state
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  
  const token = localStorage.getItem('token');

  // Handle logout and redirect to /wtadmin
  const handleLogoutClick = () => {
    onLogout();
    navigate('/wtadmin');
  };

  useEffect(() => {
    fetchDashboardData();
    fetchAbandonedLeads();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchBlacklist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/blacklist?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBlacklist(data.blacklist || []);
      }
    } catch (error) {
      console.error('Error fetching blacklist:', error);
    }
  };

  const fetchAbandonedLeads = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/abandoned-leads?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAbandonedLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching abandoned leads:', error);
    }
  };

  const fetchCancelledTenants = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/cancelled-tenants?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCancelledTenants(data.tenants || []);
      }
    } catch (error) {
      console.error('Error fetching cancelled tenants:', error);
    }
  };


  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch stats, companies, and growth data in parallel
      const [statsRes, companiesRes, growthRes] = await Promise.all([
        fetch(`${API_URL}/api/tenant/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tenant/companies?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tenant/stats/growth?months=6`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      } else {
        console.error('Stats fetch failed:', statsRes.status);
      }
      
      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        console.log('Companies fetched:', companiesData.companies?.length || 0);
        setCompanies(companiesData.companies || []);
      } else {
        console.error('Companies fetch failed:', companiesRes.status);
        // If unauthorized, the token might be invalid
        if (companiesRes.status === 401 || companiesRes.status === 403) {
          toast.error('Session expired. Please login again.');
        }
      }
      
      if (growthRes.ok) {
        const growthJson = await growthRes.json();
        setGrowthData(growthJson.growth || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to suspend this company?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tenant/companies/${companyId}/suspend`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Company suspended');
        fetchDashboardData();
      } else {
        toast.error('Failed to suspend company');
      }
    } catch (error) {
      toast.error('Error suspending company');
    }
  };

  const handleReactivateCompany = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/companies/${companyId}/reactivate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Company reactivated');
        fetchDashboardData();
      } else {
        toast.error('Failed to reactivate company');
      }
    } catch (error) {
      toast.error('Error reactivating company');
    }
  };

  // State for subscription management modal
  const [subscriptionModal, setSubscriptionModal] = useState({ open: false, company: null });
  const [subscriptionForm, setSubscriptionForm] = useState({ 
    active: true, 
    expiryDate: '', 
    reason: '',
    notify: true 
  });

  // State for stats detail modal
  const [statsModal, setStatsModal] = useState({ open: false, type: null, title: '', data: [] });

  // Get filtered data for stats modal
  const getStatsData = (type) => {
    if (!companies) return [];
    
    const now = new Date();
    
    switch(type) {
      case 'total':
        return companies.map(c => ({
          company_name: c.company_name,
          admin_email: c.admin_email,
          admin_phone: c.admin_phone || '-',
          gst_number: c.gst_number || '-',
          status: c.status,
          subscription_status: c.subscription_status || '-',
          locations: c.num_spa_locations || 0,
          therapists: c.therapist_count || 0,
          created_at: c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'
        }));
      case 'active':
        return companies.filter(c => c.status === 'active' && c.subscription_status !== 'trial').map(c => ({
          company_name: c.company_name,
          admin_email: c.admin_email,
          admin_phone: c.admin_phone || '-',
          gst_number: c.gst_number || '-',
          plan: c.subscription_plan || '-',
          locations: c.num_spa_locations || 0,
          therapists: c.therapist_count || 0,
          expires: c.subscription_ends_at ? new Date(c.subscription_ends_at).toLocaleDateString() : '-'
        }));
      case 'trial':
        return companies.filter(c => c.subscription_status === 'trial').map(c => ({
          company_name: c.company_name,
          admin_email: c.admin_email,
          admin_phone: c.admin_phone || '-',
          started: c.created_at ? new Date(c.created_at).toLocaleDateString() : '-',
          expires: c.trial_ends_at || c.subscription_ends_at ? new Date(c.trial_ends_at || c.subscription_ends_at).toLocaleDateString() : '-',
          days_left: c.subscription_ends_at ? Math.max(0, Math.ceil((new Date(c.subscription_ends_at) - now) / (1000*60*60*24))) : '-'
        }));
      case 'expired':
        return companies.filter(c => {
          if (c.subscription_status === 'expired' || c.status === 'expired') return true;
          if (c.subscription_ends_at && new Date(c.subscription_ends_at) < now) return true;
          return false;
        }).map(c => ({
          company_name: c.company_name,
          admin_email: c.admin_email,
          admin_phone: c.admin_phone || '-',
          expired_on: c.subscription_ends_at ? new Date(c.subscription_ends_at).toLocaleDateString() : '-',
          last_plan: c.subscription_plan || '-'
        }));
      case 'subscribed':
        return companies.filter(c => c.subscription_status === 'active' || (c.status === 'active' && c.subscription_plan)).map(c => ({
          company_name: c.company_name,
          admin_email: c.admin_email,
          plan: c.subscription_plan || '-',
          amount: c.subscription_amount || '-',
          expires: c.subscription_ends_at ? new Date(c.subscription_ends_at).toLocaleDateString() : '-',
          locations: c.num_spa_locations || 0,
          therapists: c.therapist_count || 0
        }));
      default:
        return [];
    }
  };

  const statsColumns = {
    total: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'admin_email', label: 'Email' },
      { key: 'admin_phone', label: 'Phone' },
      { key: 'gst_number', label: 'GST' },
      { key: 'status', label: 'Status' },
      { key: 'locations', label: 'Locations' },
      { key: 'therapists', label: 'Therapists' },
      { key: 'created_at', label: 'Created' }
    ],
    active: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'admin_email', label: 'Email' },
      { key: 'admin_phone', label: 'Phone' },
      { key: 'plan', label: 'Plan' },
      { key: 'locations', label: 'Locations' },
      { key: 'therapists', label: 'Therapists' },
      { key: 'expires', label: 'Expires' }
    ],
    trial: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'admin_email', label: 'Email' },
      { key: 'admin_phone', label: 'Phone' },
      { key: 'started', label: 'Started' },
      { key: 'expires', label: 'Expires' },
      { key: 'days_left', label: 'Days Left' }
    ],
    expired: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'admin_email', label: 'Email' },
      { key: 'admin_phone', label: 'Phone' },
      { key: 'expired_on', label: 'Expired On' },
      { key: 'last_plan', label: 'Last Plan' }
    ],
    subscribed: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'admin_email', label: 'Email' },
      { key: 'plan', label: 'Plan' },
      { key: 'expires', label: 'Expires' },
      { key: 'locations', label: 'Locations' },
      { key: 'therapists', label: 'Therapists' }
    ]
  };

  const openStatsModal = (type, title) => {
    const data = getStatsData(type);
    setStatsModal({ open: true, type, title, data });
  };

  const handleExportStats = () => {
    exportToExcel(statsModal.data, statsModal.type);
  };

  const handleToggleSubscription = async (company) => {
    const newStatus = company.subscription_active === false;
    
    try {
      const res = await fetch(`${API_URL}/api/tenant/companies/${company.company_id}/toggle-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription_active: newStatus,
          notify_tenant: true,
          reason: newStatus ? 'Subscription activated by admin' : 'Subscription deactivated by admin'
        })
      });

      if (res.ok) {
        toast.success(`Subscription ${newStatus ? 'activated' : 'deactivated'}`);
        fetchDashboardData();
      } else {
        toast.error('Failed to toggle subscription');
      }
    } catch (error) {
      toast.error('Error toggling subscription');
    }
  };

  const handleRestoreCompany = async (company) => {
    if (!window.confirm(`Restore subscription for "${company.company_name}"? This will reactivate their access.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tenant/companies/${company.company_id}/toggle-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription_active: true,
          notify_tenant: true,
          reason: 'Subscription restored by admin'
        })
      });

      if (res.ok) {
        toast.success('Company restored successfully');
        fetchDashboardData();
      } else {
        toast.error('Failed to restore company');
      }
    } catch (error) {
      toast.error('Error restoring company');
    }
  };

  const handleDeleteExpiredTenant = async (company) => {
    const confirmMessage = `⚠️ PERMANENT DELETION WARNING ⚠️

You are about to permanently delete:
• Company: ${company.company_name}
• All users, properties, therapists, bookings, payments

This action CANNOT be undone!

The following will be BLACKLISTED to prevent free trial abuse:
• Email: ${company.admin_email}
• Phone: ${company.admin_phone}
${company.gst_number ? `• GST: ${company.gst_number}` : ''}

Type "DELETE" to confirm:`;
    
    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/tenant/delete-expired-tenant/${company.company_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Tenant "${company.company_name}" deleted. ${data.deleted_counts?.users || 0} users, ${data.deleted_counts?.properties || 0} properties removed.`);
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete tenant');
      }
    } catch (error) {
      toast.error('Error deleting tenant');
    }
  };

  const handleManageSubscription = (company) => {
    setSubscriptionModal({ open: true, company });
    setSubscriptionForm({
      active: company.subscription_active !== false,
      expiryDate: company.subscription_ends_at || company.trial_ends_at 
        ? new Date(company.subscription_ends_at || company.trial_ends_at).toISOString().split('T')[0]
        : new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      reason: '',
      notify: true
    });
  };

  const handleSaveSubscription = async () => {
    if (!subscriptionModal.company) return;

    try {
      const res = await fetch(`${API_URL}/api/tenant/companies/${subscriptionModal.company.company_id}/toggle-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription_active: subscriptionForm.active,
          expiry_date: subscriptionForm.expiryDate + 'T23:59:59Z',
          reason: subscriptionForm.reason,
          notify_tenant: subscriptionForm.notify
        })
      });

      if (res.ok) {
        toast.success('Subscription updated successfully');
        setSubscriptionModal({ open: false, company: null });
        fetchDashboardData();
      } else {
        toast.error('Failed to update subscription');
      }
    } catch (error) {
      toast.error('Error updating subscription');
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchQuery || 
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.subdomain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.admin_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Match against both status and subscription_status
    const companyStatus = company.subscription_status || company.status;
    const matchesStatus = !statusFilter || 
      companyStatus === statusFilter ||
      (statusFilter === 'active' && ['active', 'subscribed', 'trial'].includes(companyStatus)) ||
      (statusFilter === 'subscribed' && companyStatus === 'subscribed') ||
      (statusFilter === 'trial' && companyStatus === 'trial') ||
      (statusFilter === 'expired' && ['expired', 'cancelled', 'past_due'].includes(companyStatus)) ||
      (statusFilter === 'suspended' && companyStatus === 'suspended');
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground animate-pulse">Loading platform dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WellNest Technologies Platform</h1>
              <p className="text-xs text-slate-400">Super Admin Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-300">{user?.email}</span>
            {/* Environment Mode Badge */}
            {settings?.platform_mode === 'test' ? (
              <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse">
                <AlertTriangle className="w-3 h-3 mr-1" />
                TEST MODE
              </Badge>
            ) : (
              <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <CheckCircle className="w-3 h-3 mr-1" />
                LIVE MODE
              </Badge>
            )}
            <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30">
              Platform Admin
            </Badge>
            <Button variant="outline" size="sm" onClick={handleLogoutClick} 
              className="border-slate-600 text-slate-300 hover:bg-slate-700">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Environment Data Banner */}
        {settings?.platform_mode === 'test' ? (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-amber-400 font-semibold">Test Environment Data</h3>
                  <p className="text-slate-300 text-sm">
                    Showing only TEST data. Switch to Live mode in Settings to see real customer data.
                  </p>
                </div>
              </div>
              <Badge className="bg-amber-500/20 text-amber-300 text-xs">
                Test Cards: 4111 1111 1111 1111
              </Badge>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-emerald-400 font-semibold">Live Production Data</h3>
                <p className="text-slate-300 text-sm">
                  Showing real customer data with live payments. All actions affect real customers.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Platform Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card 
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800/70 transition-colors"
            onClick={() => openStatsModal('total', 'All Tenants')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Tenants</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_companies || 0}</p>
                  <p className="text-xs text-emerald-400 mt-1">
                    +{stats?.new_companies_this_month || 0} this month
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> Click for details & export
              </p>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors"
            onClick={() => setShowRevenueModal(true)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Revenue</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats?.total_revenue_collected || 0)}</p>
                  <p className="text-xs text-blue-400 mt-1">
                    This month: {formatCurrency(stats?.monthly_revenue_this_month || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> Click for details & export
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Platform Bookings</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_bookings?.toLocaleString() || 0}</p>
                  <p className="text-xs text-amber-400 mt-1">
                    +{stats?.bookings_this_month || 0} this month
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Revenue Processed</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats?.total_revenue_processed || 0)}</p>
                  <p className="text-xs text-purple-400 mt-1">
                    Across all tenants
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className="bg-slate-800/30 border-slate-700 cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={() => openStatsModal('active', 'Active Subscriptions')}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.active_companies || 0}</p>
                  <p className="text-xs text-slate-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-slate-800/30 border-slate-700 cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={() => openStatsModal('trial', 'Trial Users')}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.trial_companies || 0}</p>
                  <p className="text-xs text-slate-400">Trial</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-slate-800/30 border-slate-700 cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={() => openStatsModal('expired', 'Expired/Inactive')}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {companies?.filter(c => c.subscription_status === 'expired' || c.status === 'expired' || (c.subscription_ends_at && new Date(c.subscription_ends_at) < new Date())).length || 0}
                  </p>
                  <p className="text-xs text-slate-400">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-slate-800/30 border-slate-700 cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={() => openStatsModal('subscribed', 'Paid Subscriptions')}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {companies?.filter(c => c.subscription_status === 'active' && c.subscription_status !== 'trial').length || 0}
                  </p>
                  <p className="text-xs text-slate-400">Subscribed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-slate-800/30 border-slate-700 cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={() => setTenantSubTab('cancelled')}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{cancelledTenants?.length || 0}</p>
                  <p className="text-xs text-slate-400">Cancelled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions Panel - Recent Signups Needing Follow-up */}
        <Card className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-700/50 mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                <CardTitle className="text-amber-200 text-lg">Quick Actions</CardTitle>
              </div>
              <Badge className="bg-amber-600/30 text-amber-300 border-amber-500/50">
                {(() => {
                  const recentSignups = companies?.filter(c => {
                    const createdDate = new Date(c.created_at);
                    const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                    const needsFollowup = (c.subscription_status === 'trial' || c.subscription_status === 'expired') && daysSinceCreated <= 7;
                    return needsFollowup;
                  }) || [];
                  return `${recentSignups.length} need attention`;
                })()}
              </Badge>
            </div>
            <CardDescription className="text-amber-300/70">
              Recent signups that may need onboarding follow-up
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {companies?.filter(c => {
                const createdDate = new Date(c.created_at);
                const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                const needsFollowup = (c.subscription_status === 'trial' || c.subscription_status === 'expired') && daysSinceCreated <= 7;
                return needsFollowup;
              }).slice(0, 6).map((company, idx) => {
                const createdDate = new Date(company.created_at);
                const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                return (
                  <div 
                    key={idx} 
                    className="bg-slate-800/60 rounded-lg p-3 border border-slate-700 hover:border-amber-500/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowCompanyDetails(true);
                    }}
                    data-testid={`quick-action-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{company.company_name}</p>
                        <p className="text-xs text-slate-400 truncate">{company.admin_email}</p>
                      </div>
                      <Badge className={`ml-2 text-xs ${
                        company.subscription_status === 'trial' 
                          ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' 
                          : 'bg-red-500/20 text-red-300 border-red-500/30'
                      }`}>
                        {company.subscription_status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{daysSinceCreated === 0 ? 'Today' : daysSinceCreated === 1 ? 'Yesterday' : `${daysSinceCreated} days ago`}</span>
                      {company.phone && (
                        <>
                          <span className="text-slate-600">•</span>
                          <Phone className="w-3 h-3" />
                          <span>{company.phone}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!companies?.filter(c => {
                const createdDate = new Date(c.created_at);
                const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                return (c.subscription_status === 'trial' || c.subscription_status === 'expired') && daysSinceCreated <= 7;
              }).length) && (
                <div className="col-span-full text-center py-4 text-slate-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500/50" />
                  <p>All recent signups are on track!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="companies" className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 flex-wrap h-auto p-1">
            <TabsTrigger value="companies" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <Building2 className="w-4 h-4 mr-2" /> Tenants
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Reports
            </TabsTrigger>
            <TabsTrigger value="analytics" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <DollarSign className="w-4 h-4 mr-2" /> Payments
            </TabsTrigger>
            <TabsTrigger value="pricing" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <Key className="w-4 h-4 mr-2" /> Pricing
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <Activity className="w-4 h-4 mr-2" /> Activity
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-slate-700/50">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
            {/* Marketing Tab - Only visible in LIVE mode */}
            {settings?.platform_mode === 'live' && (
              <TabsTrigger value="marketing" className="text-slate-300 data-[state=active]:bg-purple-900 data-[state=active]:text-purple-200 data-[state=inactive]:hover:text-purple-300 data-[state=inactive]:hover:bg-purple-900/30">
                <Target className="w-4 h-4 mr-2" /> Marketing
              </TabsTrigger>
            )}
          </TabsList>

          {/* Reports Tab - NEW */}
          <TabsContent value="reports">
            <ReportsPanel token={token} />
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-white">Tenant Management</CardTitle>
                    <CardDescription className="text-slate-400">
                      View registered tenants and follow up on abandoned leads
                    </CardDescription>
                  </div>
                </div>
                
                {/* Sub-tabs for Registered vs Abandoned vs Blacklist */}
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant={tenantSubTab === 'registered' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTenantSubTab('registered')}
                    className={tenantSubTab === 'registered' 
                      ? 'bg-teal-600 hover:bg-teal-700' 
                      : 'border-slate-600 text-slate-300'}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Registered Tenants ({stats?.total_companies || 0})
                  </Button>
                  <Button 
                    variant={tenantSubTab === 'abandoned' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTenantSubTab('abandoned')}
                    className={tenantSubTab === 'abandoned' 
                      ? 'bg-amber-600 hover:bg-amber-700' 
                      : 'border-slate-600 text-slate-300'}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Failed to Proceed ({abandonedLeads?.length || 0})
                  </Button>
                  <Button 
                    variant={tenantSubTab === 'cancelled' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setTenantSubTab('cancelled'); fetchCancelledTenants(); }}
                    className={tenantSubTab === 'cancelled' 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'border-slate-600 text-slate-300'}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelled ({cancelledTenants?.length || 0})
                  </Button>
                  <Button 
                    variant={tenantSubTab === 'blacklist' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setTenantSubTab('blacklist'); fetchBlacklist(); }}
                    className={tenantSubTab === 'blacklist' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'border-slate-600 text-slate-300'}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Blacklist ({blacklist?.length || 0})
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Registered Tenants Sub-Tab */}
                {tenantSubTab === 'registered' && (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search companies..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-sm text-white"
                      >
                        <option value="">All Tenants</option>
                        <option value="active">Active (Trial + Subscribed)</option>
                        <option value="trial">Trial Only</option>
                        <option value="subscribed">Subscribed Only</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                      </select>
                      
                      <Button variant="outline" size="icon" onClick={fetchDashboardData}
                        className="border-slate-600 hover:bg-slate-700">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`${API_URL}/api/tenant/companies/export/excel`, '_blank')}
                        className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                      </Button>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Company</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Contact</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Expiry</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Active</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCompanies.map((company) => (
                            <tr key={company.company_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  {company.logo_url ? (
                                    <img src={company.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                  ) : (
                                    <div className="w-8 h-8 rounded-lg bg-slate-600 flex items-center justify-center">
                                      <Building2 className="w-4 h-4 text-slate-400" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-white">{company.company_name}</p>
                                    <p className="text-xs text-slate-400">{company.subdomain}.wellnesttechnologies.com</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div>
                                  <p className="text-slate-300 text-sm">{company.admin_email}</p>
                                  <p className="text-xs text-slate-500">{company.admin_phone || '-'}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-slate-300 capitalize">
                                  {company.subscription_plan?.replace('_', ' ') || 'Trial'}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <StatusBadge status={company.subscription_status || company.status} />
                              </td>
                              <td className="py-3 px-4">
                                {company.subscription_ends_at || company.trial_ends_at ? (
                                  <div className="text-sm">
                                    <p className="text-slate-300">
                                      {new Date(company.subscription_ends_at || company.trial_ends_at).toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </p>
                                    <p className={`text-xs ${
                                      new Date(company.subscription_ends_at || company.trial_ends_at) < new Date() 
                                        ? 'text-red-400' 
                                        : new Date(company.subscription_ends_at || company.trial_ends_at) < new Date(Date.now() + 3*24*60*60*1000)
                                        ? 'text-yellow-400'
                                        : 'text-slate-500'
                                    }`}>
                                      {new Date(company.subscription_ends_at || company.trial_ends_at) < new Date() 
                                        ? 'Expired' 
                                        : `${Math.ceil((new Date(company.subscription_ends_at || company.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days left`}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-slate-500 text-sm">-</span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleToggleSubscription(company)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      company.status === 'suspended' || company.subscription_status === 'suspended' 
                                        ? 'bg-red-600' 
                                        : company.subscription_active !== false ? 'bg-teal-600' : 'bg-slate-600'
                                    }`}
                                    title={company.status === 'suspended' ? 'Click to restore' : company.subscription_active !== false ? 'Click to suspend' : 'Click to activate'}
                                  >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      (company.status === 'suspended' || company.subscription_status === 'suspended' || company.subscription_active === false) 
                                        ? 'translate-x-1' : 'translate-x-6'
                                    }`} />
                                  </button>
                                  {(company.status === 'suspended' || company.subscription_status === 'suspended') && (
                                    <Button size="sm" variant="outline"
                                      onClick={() => handleRestoreCompany(company)}
                                      className="border-green-600 text-green-400 hover:bg-green-900/30 text-xs px-2 py-1">
                                      Restore
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button size="sm" variant="outline"
                                    onClick={() => handleManageSubscription(company)}
                                    className="border-blue-600 text-blue-400 hover:bg-blue-900/30">
                                    Manage
                                  </Button>
                                  {/* Delete button - Only for expired/cancelled tenants */}
                                  {['expired', 'cancelled', 'past_due'].includes(company.subscription_status || company.status) && (
                                    <Button size="sm" variant="outline"
                                      onClick={() => handleDeleteExpiredTenant(company)}
                                      className="border-red-600 text-red-400 hover:bg-red-900/30"
                                      title="Permanently delete this expired tenant and all their data">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {filteredCompanies.length === 0 && (
                            <tr>
                              <td colSpan={7} className="py-8 text-center text-slate-500">
                                No registered tenants found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                
                {/* Abandoned Leads Sub-Tab */}
                {tenantSubTab === 'abandoned' && (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search by name, email, phone..."
                          value={abandonedSearch}
                          onChange={(e) => setAbandonedSearch(e.target.value)}
                          className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      
                      <Button variant="outline" size="icon" onClick={fetchAbandonedLeads}
                        className="border-slate-600 hover:bg-slate-700">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`${API_URL}/api/tenant/abandoned-leads/export/excel`, '_blank')}
                        className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Leads
                      </Button>
                    </div>
                    
                    <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-amber-400 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>
                          <strong>OTP Verified Only:</strong> Users who verified email but never completed registration. 
                          <strong className="ml-2">Registered - Incomplete:</strong> Users who registered but didn't start trial/checkout.
                        </span>
                      </p>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Company / Contact</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Phone</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Last Package</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Registered</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(abandonedSearch 
                            ? abandonedLeads.filter(l => 
                                l.company_name?.toLowerCase().includes(abandonedSearch.toLowerCase()) ||
                                l.admin_email?.toLowerCase().includes(abandonedSearch.toLowerCase()) ||
                                l.admin_phone?.includes(abandonedSearch)
                              )
                            : abandonedLeads
                          ).map((lead, idx) => {
                            // Determine status based on stage
                            const stage = lead.stage || 'unknown';
                            const isOtpOnly = stage === 'otp_verified';
                            const isExpired = ['expired', 'cancelled', 'past_due'].includes(lead.subscription_status);
                            
                            let statusLabel = lead.status || 'Unknown';
                            let statusColor = 'text-amber-400 bg-amber-500/20';
                            
                            if (isOtpOnly) {
                              statusLabel = 'OTP Verified Only';
                              statusColor = 'text-blue-400 bg-blue-500/20';
                            } else if (isExpired) {
                              statusLabel = 'Expired';
                              statusColor = 'text-red-400 bg-red-500/20';
                            }
                            
                            const lastPackage = lead.subscription_plan || lead.selected_plan || 'N/A';
                            
                            return (
                              <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="font-medium text-white">{lead.company_name || '-'}</p>
                                    <p className="text-xs text-slate-400">{lead.admin_name || '-'}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <a href={`mailto:${lead.admin_email}`} className="text-blue-400 hover:underline text-sm">
                                    {lead.admin_email}
                                  </a>
                                </td>
                                <td className="py-3 px-4">
                                  <a href={`tel:${lead.admin_phone}`} className="text-emerald-400 hover:underline text-sm font-medium">
                                    {lead.admin_phone || '-'}
                                  </a>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                    {statusLabel}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-slate-300 text-sm capitalize">
                                    {lastPackage?.replace('_', ' ') || '-'}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-slate-400 text-sm">
                                  {lead.registered_at ? new Date(lead.registered_at).toLocaleDateString('en-IN') : '-'}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30 text-xs"
                                    onClick={() => window.open(`tel:${lead.admin_phone}`, '_blank')}
                                  >
                                    <Phone className="w-3 h-3 mr-1" />
                                    Call
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                          {abandonedLeads.length === 0 && (
                            <tr>
                              <td colSpan={7} className="py-8 text-center text-slate-500">
                                No abandoned leads found. Great news - everyone completed their registration!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Cancelled Sub-Tab */}
                {tenantSubTab === 'cancelled' && (
                  <>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-orange-400 mt-0.5" />
                        <div>
                          <h4 className="text-orange-400 font-medium">Cancelled Subscriptions</h4>
                          <p className="text-sm text-slate-400 mt-1">
                            Tenants who have cancelled their subscription. Service continues until expiry date.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <div className="relative flex-1 max-w-xs">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search cancelled tenants..."
                          value={cancelledSearch}
                          onChange={(e) => setCancelledSearch(e.target.value)}
                          className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={fetchCancelledTenants}
                        className="border-slate-600 hover:bg-slate-700"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Company</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Admin</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Cancelled At</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Expires At</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cancelledSearch 
                            ? cancelledTenants.filter(t => 
                                t.company_name?.toLowerCase().includes(cancelledSearch.toLowerCase()) ||
                                t.admin_email?.toLowerCase().includes(cancelledSearch.toLowerCase())
                              )
                            : cancelledTenants
                          ).map((tenant, idx) => (
                            <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-lg bg-orange-900/30 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-orange-400" />
                                  </div>
                                  <div>
                                    <span className="font-medium text-white block">{tenant.company_name}</span>
                                    <span className="text-xs text-slate-500">{tenant.subdomain}.wellnesttechnologies.com</span>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div>
                                  <span className="text-slate-300 text-sm block">{tenant.admin_email}</span>
                                  <span className="text-xs text-slate-500">{tenant.admin_phone}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-slate-300 text-sm">
                                  {tenant.cancellation_requested_at 
                                    ? new Date(tenant.cancellation_requested_at).toLocaleDateString('en-IN', {
                                        day: 'numeric', month: 'short', year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-amber-400 text-sm">
                                  {tenant.subscription_ends_at 
                                    ? new Date(tenant.subscription_ends_at).toLocaleDateString('en-IN', {
                                        day: 'numeric', month: 'short', year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="max-w-xs">
                                  <span className="text-slate-300 text-sm italic">
                                    "{tenant.cancellation_reason || 'No reason provided'}"
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {cancelledTenants.length === 0 && (
                            <tr>
                              <td colSpan={5} className="py-8 text-center text-slate-500">
                                No cancelled tenants found. All subscriptions are active.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Blacklist Sub-Tab */}
                {tenantSubTab === 'blacklist' && (
                  <>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-red-400 mt-0.5" />
                        <div>
                          <h4 className="text-red-400 font-medium">Blacklisted Tenants</h4>
                          <p className="text-sm text-slate-400 mt-1">
                            These are deleted tenants who cannot register for a free trial again. 
                            They can still subscribe with a paid plan if they contact support.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <div className="relative flex-1 max-w-xs">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search blacklist..."
                          value={blacklistSearch}
                          onChange={(e) => setBlacklistSearch(e.target.value)}
                          className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={fetchBlacklist}
                        className="border-slate-600 hover:bg-slate-700"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Company</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Admin Email</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Phone</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">GST Number</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Reason</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Deleted At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(blacklistSearch 
                            ? blacklist.filter(b => 
                                b.company_name?.toLowerCase().includes(blacklistSearch.toLowerCase()) ||
                                b.admin_email?.toLowerCase().includes(blacklistSearch.toLowerCase()) ||
                                b.admin_phone?.includes(blacklistSearch) ||
                                b.gst_number?.toLowerCase().includes(blacklistSearch.toLowerCase())
                              )
                            : blacklist
                          ).map((entry, idx) => (
                            <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-red-400" />
                                  </div>
                                  <span className="font-medium text-white">{entry.company_name || '-'}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-slate-300 text-sm">{entry.admin_email || '-'}</span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-slate-300 text-sm">{entry.admin_phone || '-'}</span>
                              </td>
                              <td className="py-3 px-4">
                                <code className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
                                  {entry.gst_number || 'N/A'}
                                </code>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-red-400 bg-red-500/20 px-2 py-1 rounded-full">
                                  {entry.reason?.replace(/_/g, ' ') || 'Deleted'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-slate-400 text-sm">
                                {entry.deleted_at 
                                  ? new Date(entry.deleted_at).toLocaleDateString('en-IN', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : '-'
                                }
                              </td>
                            </tr>
                          ))}
                          {blacklist.length === 0 && (
                            <tr>
                              <td colSpan={6} className="py-8 text-center text-slate-500">
                                No blacklisted tenants found. The blacklist is empty.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Platform Growth</CardTitle>
                <CardDescription className="text-slate-400">
                  Monthly trends across all tenants
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {growthData.map((month, index) => (
                    <Card key={index} className="bg-slate-700/30 border-slate-600">
                      <CardContent className="pt-4">
                        <p className="text-sm font-medium text-slate-400 mb-4">{month.month}</p>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-300">New Companies</span>
                            <span className="font-bold text-emerald-400">{month.new_companies}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-300">Bookings</span>
                            <span className="font-bold text-blue-400">{month.bookings}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-300">Revenue</span>
                            <span className="font-bold text-amber-400">{formatCurrency(month.revenue)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentsPanel token={token} />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <PricingPanel token={token} />
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
                <CardDescription className="text-slate-400">
                  Platform-wide activity log
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-slate-400">
                  Activity feed coming soon...
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsPanel user={user} token={token} />
          </TabsContent>

          {/* Marketing Tab - Only in LIVE mode */}
          {settings?.platform_mode === 'live' && (
            <TabsContent value="marketing">
              <MarketingPanel token={token} settings={settings} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Revenue Detail Modal */}
      <RevenueDetailModal 
        isOpen={showRevenueModal} 
        onClose={() => setShowRevenueModal(false)} 
        token={token}
      />

      {/* Subscription Management Modal */}
      {subscriptionModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">Manage Subscription</h3>
            <p className="text-slate-400 mb-6">{subscriptionModal.company?.company_name}</p>
            
            <div className="space-y-4">
              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Subscription Active</span>
                <button
                  onClick={() => setSubscriptionForm(f => ({ ...f, active: !f.active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    subscriptionForm.active ? 'bg-teal-600' : 'bg-slate-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    subscriptionForm.active ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Expiry Date */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Expiry Date</label>
                <Input
                  type="date"
                  value={subscriptionForm.expiryDate}
                  onChange={(e) => setSubscriptionForm(f => ({ ...f, expiryDate: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Reason (optional)</label>
                <Input
                  value={subscriptionForm.reason}
                  onChange={(e) => setSubscriptionForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g., Payment verified manually"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Notify Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={subscriptionForm.notify}
                  onChange={(e) => setSubscriptionForm(f => ({ ...f, notify: e.target.checked }))}
                  className="rounded border-slate-600"
                />
                <span className="text-sm text-slate-300">Notify tenant via email</span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300"
                onClick={() => setSubscriptionModal({ open: false, company: null })}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={handleSaveSubscription}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Detail Modal */}
      <StatsDetailModal 
        isOpen={statsModal.open}
        onClose={() => setStatsModal({ open: false, type: null, title: '', data: [] })}
        title={statsModal.title}
        data={statsModal.data}
        columns={statsColumns[statsModal.type] || []}
        onExport={handleExportStats}
      />
    </div>
  );
}
